import { ExternalLink, Eye, ListFilter } from "lucide-react";
import { type FormEvent, useEffect, useState } from "react";

import { TUNABLES } from "../../shared/tunables";
import { type TunableGetter, useTunables } from "../lib/tunables";
import { Button, EmptyState, formatKr, HowItWorks, Money, Page, Point, SectionTitle, StateLabel } from "../ui";

type WatchView = {
	id: number;
	kind: "url" | "rule";
	priority: "hot" | "normal" | "cold";
	base_interval_ms: number;
	status: string;
	product_id: number | null;
	product_url: string | null;
	product_title: string | null;
	store_id: string | null;
	last_run_at: number | null;
	next_run_at: number;
};

type Rule = {
	id: number;
	label: string;
	keywordGlob: string | null;
	categoryGlob: string | null;
	storeFilter: string | null;
	maxPriceMinor: number | null;
	autoBuy: boolean;
	autoBuyQty: number;
	autoBuyRail: "vipps" | "klarna" | null;
	enabled: boolean;
	maxDailyMinor: number | null;
	/** Auto-buy spend so far today (Oslo), counted against maxDailyMinor. */
	spentMinor: number;
};

/** The only stores with a buy flow (src/server/autobuy/buy-loop.ts). */
const AUTO_BUY_STORES = ["pokechest", "ringo"];

const RAIL_LABEL = { vipps: "Vipps", klarna: "Klarna" } as const;

/** "1 500" or "1500,50" kr → øre. Blank → null; anything unparseable → NaN. */
function krToMinor(v: string): number | null {
	const t = v.trim().replace(/\s/g, "").replace(",", ".");
	if (t === "") return null;
	return Math.round(Number(t) * 100);
}

const minorToKr = (m: number | null) => (m == null ? "" : String(m / 100));

async function fetchJson<T>(url: string, init?: RequestInit): Promise<T> {
	const res = await fetch(url, {
		headers: init?.body ? { "content-type": "application/json" } : undefined,
		...init,
	});
	return (await res.json()) as T;
}

function fmtInterval(ms: number): string {
	if (ms < 60_000) return `${Math.round(ms / 1000)}s`;
	if (ms < 3_600_000) return `${Math.round(ms / 60_000)} min`;
	return `${Math.round(ms / 3_600_000)} h`;
}

/** The gap between checks for a tier, as Settings sets it — the same
 *  values nextRunAt in server/scheduler/scheduler.ts reads. */
function checkIntervalMs(get: TunableGetter, priority: WatchView["priority"]): number {
	if (priority === "hot") return get(TUNABLES.watchHot) * 1000;
	if (priority === "normal") return get(TUNABLES.watchNormal) * 60_000;
	return get(TUNABLES.watchCold) * 60_000;
}

const STATUS_WORD: Record<string, string> = {
	active: "",
	cooldown: "last check failed, retrying",
};

/** Every text field and select on the page: 32px on a mouse, the 44px
 *  touch floor on a finger (min-h-tap). */
const inputClass =
	"min-h-tap rounded-md border border-hairline bg-ground px-2.5 text-[13px] text-ink placeholder:text-muted outline-none focus:border-line";

/** Small inline actions on a row: h-7 on a mouse, the touch floor on a
 *  finger. */
const rowAction = "pointer-coarse:min-h-11";

function AddWatchForm({ onAdded }: { onAdded: () => void }) {
	const { get } = useTunables();
	const [url, setUrl] = useState("");
	const [priority, setPriority] = useState<"hot" | "normal" | "cold">("normal");
	const [submitting, setSubmitting] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const submit = async (e: FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		setError(null);
		setSubmitting(true);
		try {
			const res = await fetchJson<{ error?: string }>("/api/watchlist", {
				method: "POST",
				body: JSON.stringify({ url, priority }),
			});
			if (res.error) setError(res.error);
			else {
				setUrl("");
				onAdded();
			}
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<form
			onSubmit={submit}
			className="mb-2 flex flex-col gap-2 rounded-[10px] border border-hairline bg-paper p-3 sm:flex-row sm:flex-wrap sm:items-center"
		>
			<input
				required
				type="url"
				placeholder="https://www.pokechest.no/produkt/…"
				value={url}
				onChange={(e) => setUrl(e.target.value)}
				className={`${inputClass} w-full sm:min-w-48 sm:flex-1`}
			/>
			<div className="flex gap-2">
				<select
					value={priority}
					onChange={(e) => setPriority(e.target.value as "hot" | "normal" | "cold")}
					className={`${inputClass} min-w-0 flex-1 pr-1 sm:flex-initial`}
				>
					<option value="hot">Hot: about every {fmtInterval(checkIntervalMs(get, "hot"))}</option>
					<option value="normal">Normal: about every {fmtInterval(checkIntervalMs(get, "normal"))}</option>
					<option value="cold">Cold: about every {fmtInterval(checkIntervalMs(get, "cold"))}</option>
				</select>
				<Button type="submit" variant="primary" disabled={submitting || !url} className="shrink-0">
					{submitting ? "Adding…" : "Track URL"}
				</Button>
			</div>
			{error && (
				<p className="flex w-full flex-wrap items-baseline gap-1.5">
					<StateLabel state="fault" label="Failed" />
					<span className="text-xs break-words text-muted">{error}</span>
				</p>
			)}
		</form>
	);
}

/**
 * Create a rule, or edit one when `initial` is given. An edit sends every
 * field, blanks as null, so clearing an input clears the stored value.
 */
function RuleForm({
	initial,
	customerComplete,
	onSaved,
	onCancel,
}: {
	initial?: Rule;
	customerComplete: boolean | null;
	onSaved: () => void;
	onCancel?: () => void;
}) {
	const [label, setLabel] = useState(initial?.label ?? "");
	const [keyword, setKeyword] = useState(initial?.keywordGlob ?? "");
	const [category, setCategory] = useState(initial?.categoryGlob ?? "");
	const [stores, setStores] = useState(initial?.storeFilter ?? "");
	const [maxPrice, setMaxPrice] = useState(minorToKr(initial?.maxPriceMinor ?? null));
	const [autoBuy, setAutoBuy] = useState(initial?.autoBuy ?? false);
	const [qty, setQty] = useState(String(initial?.autoBuyQty ?? 1));
	const [rail, setRail] = useState<"" | "vipps" | "klarna">(initial?.autoBuyRail ?? "");
	const [cap, setCap] = useState(minorToKr(initial?.maxDailyMinor ?? null));
	const [submitting, setSubmitting] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const storeList = stores
		.split(",")
		.map((s) => s.trim())
		.filter(Boolean);
	const buyableStores = storeList.filter((s) => AUTO_BUY_STORES.includes(s));

	const submit = async (e: FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		const maxPriceMinor = krToMinor(maxPrice);
		const maxDailyMinor = krToMinor(cap);
		if (Number.isNaN(maxPriceMinor)) return setError("Max price isn't a number.");
		if (Number.isNaN(maxDailyMinor) || (maxDailyMinor != null && maxDailyMinor <= 0)) {
			return setError("Daily cap must be more than 0 kr, or blank for no cap.");
		}
		setError(null);
		setSubmitting(true);
		try {
			const res = await fetchJson<{ error?: string }>(initial ? `/api/rules/${initial.id}` : "/api/rules", {
				method: initial ? "PATCH" : "POST",
				body: JSON.stringify({
					label: label.trim(),
					keywordGlob: keyword || null,
					categoryGlob: category || null,
					storeFilter: stores || null,
					maxPriceMinor,
					autoBuy,
					autoBuyQty: Math.max(1, Math.floor(Number(qty)) || 1),
					autoBuyRail: rail || null,
					maxDailyMinor,
				}),
			});
			if (res.error) return setError(res.error);
			if (!initial) {
				setLabel("");
				setKeyword("");
				setCategory("");
				setStores("");
				setMaxPrice("");
				setAutoBuy(false);
				setQty("1");
				setRail("");
				setCap("");
			}
			onSaved();
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<form
			onSubmit={submit}
			className="grid grid-cols-1 gap-2 rounded-[10px] border border-hairline bg-paper p-3 sm:grid-cols-2 lg:grid-cols-6"
		>
			<input
				required
				placeholder="Label"
				aria-label="Label"
				value={label}
				onChange={(e) => setLabel(e.target.value)}
				className={`${inputClass} sm:col-span-2`}
			/>
			<input
				placeholder="Title or category glob, e.g. *151* booster*"
				aria-label="Keyword glob"
				value={keyword}
				onChange={(e) => setKeyword(e.target.value)}
				className={`${inputClass} lg:col-span-2`}
			/>
			<input
				placeholder="Category glob, e.g. *booster box*"
				aria-label="Category glob"
				value={category}
				onChange={(e) => setCategory(e.target.value)}
				className={`${inputClass} lg:col-span-2`}
			/>
			<input
				placeholder="Stores (csv), blank = all"
				aria-label="Stores"
				value={stores}
				onChange={(e) => setStores(e.target.value)}
				className={`${inputClass} lg:col-span-4`}
			/>
			<input
				inputMode="decimal"
				placeholder="Max price, kr"
				aria-label="Max price in kr"
				value={maxPrice}
				onChange={(e) => setMaxPrice(e.target.value)}
				className={`${inputClass} lg:col-span-2`}
			/>

			{/* Off, auto-buy is one checkbox sharing a row with the buttons. On, it
          spends money, so it opens into a foil-bordered box of its own. */}
			<fieldset
				className={`flex flex-col gap-2 rounded-lg border ${
					autoBuy ? "col-span-full border-foil p-3" : "border-transparent sm:col-span-1 lg:col-span-3"
				}`}
			>
				<label className="flex min-h-tap w-fit items-center gap-2 text-[13px] font-medium text-ink">
					<input
						type="checkbox"
						checked={autoBuy}
						onChange={(e) => setAutoBuy(e.target.checked)}
						className="size-4 accent-ink"
					/>
					Auto-buy on match
				</label>
				{autoBuy && (
					<>
						<div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
							<label className="flex flex-col gap-1 text-xs text-muted">
								Quantity
								<input
									type="number"
									min={1}
									step={1}
									value={qty}
									onChange={(e) => setQty(e.target.value)}
									className={inputClass}
								/>
							</label>
							<label className="flex flex-col gap-1 text-xs text-muted">
								Pay with
								<select
									value={rail}
									onChange={(e) => setRail(e.target.value as "" | "vipps" | "klarna")}
									className={inputClass}
								>
									<option value="">Store default (Vipps)</option>
									<option value="vipps">Vipps</option>
									<option value="klarna">Klarna</option>
								</select>
							</label>
							<label className="flex flex-col gap-1 text-xs text-muted">
								Daily spend cap, kr
								<input
									inputMode="decimal"
									placeholder="No cap"
									value={cap}
									onChange={(e) => setCap(e.target.value)}
									className={inputClass}
								/>
							</label>
						</div>
						<ul className="grid list-disc gap-x-6 gap-y-1 pl-4 text-xs text-muted lg:grid-cols-2">
							<li>
								Only buys at <span className="text-ink">Pokechest</span> and <span className="text-ink">Ringo</span>.
								Matches at other stores still alert, but their buy attempts fail.
							</li>
							<li>Stops at the Vipps or Klarna screen. Nothing is paid until you approve it there.</li>
							<li>
								The cap counts price × quantity per rule and resets at midnight, Oslo time. A buy that would go over it
								is skipped.
							</li>
							<li>
								Uses the customer details from{" "}
								<a href="#/manage/settings" className="text-ink underline">
									Settings
								</a>
								.
							</li>
						</ul>
						{customerComplete === false && (
							<p className="flex flex-wrap items-baseline gap-1.5 text-xs">
								<StateLabel state="needs" label="Customer details missing" />
								<span className="text-muted">
									Every buy fails until you fill them in under{" "}
									<a href="#/manage/settings" className="text-ink underline">
										Settings
									</a>
									.
								</span>
							</p>
						)}
						{storeList.length > 0 && buyableStores.length === 0 && (
							<p className="flex flex-wrap items-baseline gap-1.5 text-xs">
								<StateLabel state="needs" label="Can't auto-buy" />
								<span className="text-muted">None of these stores has a buy flow. Add pokechest or ringo.</span>
							</p>
						)}
					</>
				)}
			</fieldset>

			{error && (
				<p className="col-span-full flex flex-wrap items-baseline gap-1.5">
					<StateLabel state="fault" label="Not saved" />
					<span className="text-xs break-words text-muted">{error}</span>
				</p>
			)}
			<div
				className={`flex items-center gap-2 sm:justify-end ${
					autoBuy ? "col-span-full" : "sm:col-span-1 lg:col-span-3"
				}`}
			>
				{onCancel && (
					<Button type="button" onClick={onCancel} className="flex-1 sm:flex-initial">
						Cancel
					</Button>
				)}
				<Button
					type="submit"
					variant="primary"
					disabled={!label.trim() || submitting}
					className="flex-1 sm:flex-initial"
				>
					{submitting ? "Saving…" : initial ? "Save rule" : "Create rule"}
				</Button>
			</div>
		</form>
	);
}

export function Watchlist() {
	const { get } = useTunables();
	const [watches, setWatches] = useState<WatchView[]>([]);
	const [rules, setRules] = useState<Rule[]>([]);
	const [editingId, setEditingId] = useState<number | null>(null);
	const [customerComplete, setCustomerComplete] = useState<boolean | null>(null);

	const refresh = async () => {
		const [w, r] = await Promise.all([
			fetchJson<{ watches: WatchView[] }>("/api/watchlist"),
			fetchJson<{ rules: Rule[] }>("/api/rules"),
		]);
		setWatches(w.watches);
		setRules(r.rules);
	};

	useEffect(() => {
		refresh();
		fetchJson<{ complete?: boolean }>("/api/customer")
			.then((c) => setCustomerComplete(c.complete ?? null))
			.catch(() => setCustomerComplete(null));
	}, []);

	const removeWatch = async (id: number) => {
		await fetch(`/api/watchlist/${id}`, { method: "DELETE" });
		refresh();
	};

	const setPriority = async (id: number, priority: string) => {
		await fetch(`/api/watchlist/${id}`, {
			method: "PATCH",
			headers: { "content-type": "application/json" },
			body: JSON.stringify({ priority }),
		});
		refresh();
	};

	const removeRule = async (id: number) => {
		await fetch(`/api/rules/${id}`, { method: "DELETE" });
		refresh();
	};

	const toggleRule = async (r: Rule) => {
		await fetch(`/api/rules/${r.id}`, {
			method: "PATCH",
			headers: { "content-type": "application/json" },
			body: JSON.stringify({ enabled: !r.enabled }),
		});
		refresh();
	};

	return (
		<Page
			title="Watchlist"
			help={
				<HowItWorks>
					<Point lead="Watching a product">
						checks its page on its own schedule, much more often than the store's sweep: hot about every{" "}
						{fmtInterval(checkIntervalMs(get, "hot"))}, normal about every {fmtInterval(checkIntervalMs(get, "normal"))}
						, cold about every {fmtInterval(checkIntervalMs(get, "cold"))}, give or take a quarter so checks don't line
						up. Use hot for drops you want to catch fast. The intervals are set under{" "}
						<a href="#/manage/settings" className="text-ink underline">
							Settings
						</a>
						.
					</Point>
					<Point lead="Every change on a watched product">
						goes to Discord: back in stock, price drops, and restocks in the Nille and Ringo shops you picked under
						Settings.
					</Point>
					<Point lead="If a check fails,">
						the watch waits and tries again: after 2 minutes for a failed page, 1 minute when the store is refusing
						requests, 5 if the store can't be read at all.
					</Point>
					<Point lead="Rules">
						are checked against every product any sweep or watch sees, whether or not you watch it: new products,
						restocks and price drops. A match goes to Discord tagged with the rule's name. A rule doesn't make anything
						checked more often; watch the product for that.
					</Point>
					<Point lead="Keywords">
						match the title or the store's category, ignoring case. * stands for anything, so pitch black*elite trainer
						matches "Pokémon Pitch Black Elite Trainer Box". Every field you fill in must match; empty fields are
						ignored.
					</Point>
					<Point lead="Auto-buy">
						on a rule starts Buy now by itself on a match, at Pokechest and Ringo, and still stops at Vipps or Klarna
						for you to approve. The daily cap counts what's been started today and resets at midnight.
					</Point>
				</HowItWorks>
			}
			subtitle="Products checked more often than a sweep, and rules that decide which products send an alert or get bought."
			width="full"
		>
			{/* URL watches on the left, rules on the right: side by side at lg,
          stacked below it. Rules get the wider column for their form. */}
			<div className="grid grid-cols-1 gap-x-5 gap-y-6 lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)]">
				<section className="min-w-0">
					<SectionTitle aside={watches.length > 0 && <span className="text-xs text-muted">{watches.length}</span>}>
						URL watches
					</SectionTitle>
					<AddWatchForm onAdded={refresh} />
					{watches.length === 0 ? (
						<EmptyState
							icon={Eye}
							title="No URL watches yet."
							description="Paste a product's address from one of your stores to check it every few minutes."
						/>
					) : (
						<ul className="divide-y divide-hairline rounded-[10px] border border-hairline bg-paper">
							{watches.map((w) => (
								<li key={w.id} className="flex flex-col gap-2 px-3 py-2 sm:flex-row sm:items-center sm:gap-3">
									<div className="min-w-0 flex-1">
										<div className="text-[13px] font-medium break-words text-ink sm:truncate">
											{w.product_title ?? w.product_url ?? `Watch #${w.id}`}
										</div>
										<div className="mt-0.5 flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-1 text-xs text-muted">
											{w.store_id && (
												<span className="rounded-full bg-raised px-2 py-0.5 text-[11px] font-medium text-muted">
													{w.store_id}
												</span>
											)}
											<span>every ~{fmtInterval(checkIntervalMs(get, w.priority))}</span>
											{(STATUS_WORD[w.status] ?? w.status) && (
												<>
													<span aria-hidden>·</span>
													<span>{STATUS_WORD[w.status] ?? w.status}</span>
												</>
											)}
											{w.product_url && (
												<>
													<span aria-hidden>·</span>
													<a
														href={w.product_url}
														target="_blank"
														rel="noreferrer"
														className="inline-flex items-center gap-1 text-ink hover:underline"
													>
														Open <ExternalLink className="size-3" aria-hidden />
													</a>
												</>
											)}
										</div>
									</div>
									<div className="flex shrink-0 items-center gap-1.5">
										<select
											value={w.priority}
											onChange={(e) => setPriority(w.id, e.target.value)}
											aria-label="Priority"
											className={`h-7 flex-1 rounded-md border border-hairline bg-ground px-2 text-xs text-ink hover:border-line sm:flex-initial ${rowAction}`}
										>
											<option value="hot">Hot</option>
											<option value="normal">Normal</option>
											<option value="cold">Cold</option>
										</select>
										<Button size="sm" onClick={() => removeWatch(w.id)} className={rowAction}>
											Remove
										</Button>
									</div>
								</li>
							))}
						</ul>
					)}
				</section>

				<section className="min-w-0">
					<SectionTitle aside={rules.length > 0 && <span className="text-xs text-muted">{rules.length}</span>}>
						Rules
					</SectionTitle>
					<div className="mb-2">
						<RuleForm customerComplete={customerComplete} onSaved={refresh} />
					</div>
					{rules.length === 0 ? (
						<EmptyState
							icon={ListFilter}
							title="No rules yet."
							description="Use the form above to create one (keyword, category, stores, price cap, auto-buy)."
						/>
					) : (
						<ul className="divide-y divide-hairline overflow-hidden rounded-[10px] border border-hairline bg-paper">
							{rules.map((r) =>
								editingId === r.id ? (
									<li key={r.id} className="bg-ground p-2">
										<RuleForm
											initial={r}
											customerComplete={customerComplete}
											onSaved={() => {
												setEditingId(null);
												refresh();
											}}
											onCancel={() => setEditingId(null)}
										/>
									</li>
								) : (
									<li key={r.id} className="flex flex-col gap-2 px-3 py-2 sm:flex-row sm:items-center sm:gap-3">
										<div className="min-w-0 flex-1">
											<div className="flex flex-wrap items-center gap-1.5">
												<span className={`text-[13px] font-medium ${r.enabled ? "text-ink" : "text-muted"}`}>
													{r.label}
												</span>
												{r.autoBuy && (
													<span className="rounded-full px-2 py-0.5 text-[11px] font-semibold bg-foil">
														Auto-buy ×{r.autoBuyQty}
														{" · "}
														{r.autoBuyRail ? RAIL_LABEL[r.autoBuyRail] : "store default"}
													</span>
												)}
											</div>
											<div className="text-xs break-words text-muted sm:truncate">
												{[
													r.keywordGlob && `title ~ ${r.keywordGlob}`,
													r.categoryGlob && `category ~ ${r.categoryGlob}`,
													r.storeFilter && `stores ${r.storeFilter}`,
												]
													.filter(Boolean)
													.join(" · ")}
												{r.maxPriceMinor != null && (
													<>
														{(r.keywordGlob || r.categoryGlob || r.storeFilter) && " · "}
														≤ <Money minor={r.maxPriceMinor} className="text-ink" />
													</>
												)}
											</div>
											{r.autoBuy && (
												<div className="text-xs text-muted">
													{r.maxDailyMinor != null ? (
														<>
															Spent <Money minor={r.spentMinor} className="text-ink" /> of{" "}
															<Money minor={r.maxDailyMinor} className="text-ink" /> today
														</>
													) : (
														<>
															No daily cap · spent <Money minor={r.spentMinor} className="text-ink" /> today
														</>
													)}
												</div>
											)}
										</div>
										<div className="flex shrink-0 items-center gap-1.5">
											<button
												onClick={() => toggleRule(r)}
												aria-pressed={r.enabled}
												className={`inline-flex h-7 flex-1 items-center justify-center rounded-md border px-2.5 text-xs transition-colors sm:flex-initial ${rowAction} ${
													r.enabled
														? "border-line bg-raised font-semibold text-ink"
														: "border-hairline bg-paper font-medium text-muted hover:border-line hover:text-ink"
												}`}
											>
												{r.enabled ? "Enabled" : "Disabled"}
											</button>
											<Button size="sm" onClick={() => setEditingId(r.id)} className={rowAction}>
												Edit
											</Button>
											<Button size="sm" onClick={() => removeRule(r.id)} className={rowAction}>
												Remove
											</Button>
										</div>
									</li>
								),
							)}
						</ul>
					)}
				</section>
			</div>
		</Page>
	);
}

export function Placeholder({ title, note }: { title: string; note: string }) {
	return (
		<div className="mx-auto max-w-3xl px-4 py-12 text-center sm:px-6 sm:py-16">
			<h1 className="mb-2 text-lg font-semibold text-ink sm:text-xl">{title}</h1>
			<p className="text-sm text-muted">{note}</p>
		</div>
	);
}
